import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  ArrowLeft, MessageSquare, Clock, AlertTriangle, CheckCircle, XCircle, 
  Loader2, Shield, RefreshCw, Filter, Search, User, ChevronDown,
  BarChart3, AlertCircle
} from 'lucide-react';
import api from '../../services/api';
import { TicketConversation } from '../../components/Tickets';

const AdminTickets = () => {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingTicket, setLoadingTicket] = useState(false);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const fetchTickets = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (categoryFilter !== 'all') params.append('category', categoryFilter);
      if (priorityFilter !== 'all') params.append('priority', priorityFilter);
      
      const [ticketsRes, statsRes] = await Promise.all([
        api.get(`/admin/tickets?${params.toString()}`),
        api.get('/admin/tickets/stats')
      ]);
      
      setTickets(ticketsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching tickets:', error);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, categoryFilter, priorityFilter]);

  const fetchTicketDetails = useCallback(async (id) => {
    setLoadingTicket(true);
    try {
      const response = await api.get(`/tickets/${id}`);
      setSelectedTicket(response.data.ticket);
      setMessages(response.data.messages);
    } catch (error) {
      console.error('Error fetching ticket details:', error);
      navigate('/admin/tickets');
    } finally {
      setLoadingTicket(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  useEffect(() => {
    if (ticketId) {
      fetchTicketDetails(ticketId);
    } else {
      setSelectedTicket(null);
      setMessages([]);
    }
  }, [ticketId, fetchTicketDetails]);

  const handleNewMessage = () => {
    if (ticketId) {
      fetchTicketDetails(ticketId);
      fetchTickets(); // Refresh list too
    }
  };

  const handleStatusChange = async (newStatus) => {
    if (!selectedTicket) return;
    
    try {
      await api.put(`/admin/tickets/${selectedTicket.id}/status`, { status: newStatus });
      fetchTicketDetails(selectedTicket.id);
      fetchTickets();
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Erreur lors de la mise à jour du statut');
    }
  };

  const handlePriorityChange = async (newPriority) => {
    if (!selectedTicket) return;
    
    try {
      await api.put(`/admin/tickets/${selectedTicket.id}/priority`, { priority: newPriority });
      fetchTicketDetails(selectedTicket.id);
      fetchTickets();
    } catch (error) {
      console.error('Error updating priority:', error);
      alert('Erreur lors de la mise à jour de la priorité');
    }
  };

  const statusLabels = {
    open: { label: 'Ouvert', color: '#00FF88', icon: <MessageSquare className="h-4 w-4" /> },
    in_progress: { label: 'En cours', color: '#FFB800', icon: <Loader2 className="h-4 w-4" /> },
    waiting_response: { label: 'Attente réponse', color: '#00FFFF', icon: <Clock className="h-4 w-4" /> },
    resolved: { label: 'Résolu', color: '#00FF88', icon: <CheckCircle className="h-4 w-4" /> },
    closed: { label: 'Fermé', color: '#6B6B6E', icon: <XCircle className="h-4 w-4" /> }
  };

  const categoryLabels = {
    technical: { label: 'Technique', color: '#00FFFF' },
    billing: { label: 'Facturation', color: '#FFB800' },
    account: { label: 'Compte', color: '#FF6B35' },
    other: { label: 'Autre', color: '#B8B8BA' }
  };

  const priorityLabels = {
    low: { label: 'Basse', color: '#00FF88' },
    medium: { label: 'Moyenne', color: '#FFB800' },
    high: { label: 'Haute', color: '#FF6B35' },
    urgent: { label: 'Urgente', color: '#FF3B5C' }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Filtrer les tickets par recherche
  const filteredTickets = tickets.filter(ticket => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      ticket.ticket_number.toLowerCase().includes(query) ||
      ticket.subject.toLowerCase().includes(query) ||
      ticket.user_email.toLowerCase().includes(query) ||
      ticket.user_name.toLowerCase().includes(query)
    );
  });

  // Vue détaillée d'un ticket
  if (ticketId && selectedTicket) {
    const status = statusLabels[selectedTicket.status] || statusLabels.open;
    const priority = priorityLabels[selectedTicket.priority] || priorityLabels.medium;
    const category = categoryLabels[selectedTicket.category] || categoryLabels.other;

    return (
      <div className="min-h-screen bg-[#121214] py-8 px-4">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <Link 
              to="/admin/tickets"
              className="inline-flex items-center gap-2 text-[#B8B8BA] hover:text-white mb-4"
            >
              <ArrowLeft className="h-4 w-4" />
              Retour aux tickets
            </Link>

            <div className="bg-[#1A1A1D] border border-[#2A2A2D] rounded-xl p-6">
              <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
                <div>
                  <div className="flex items-center gap-3 mb-2 flex-wrap">
                    <span className="text-[#FF6B35] font-mono text-lg">{selectedTicket.ticket_number}</span>
                    <span 
                      className="px-2 py-0.5 rounded text-xs font-medium"
                      style={{ backgroundColor: `${category.color}20`, color: category.color }}
                    >
                      {category.label}
                    </span>
                  </div>
                  <h1 className="text-2xl font-bold text-white">{selectedTicket.subject}</h1>
                </div>
              </div>

              {/* User info */}
              <div className="flex items-center gap-4 p-3 bg-[#222226] rounded-lg mb-4">
                <User className="h-10 w-10 text-[#00FFFF] p-2 bg-[#00FFFF]/10 rounded-full" />
                <div>
                  <p className="text-white font-medium">{selectedTicket.user_name}</p>
                  <p className="text-sm text-[#6B6B6E]">{selectedTicket.user_email}</p>
                </div>
                <span className="ml-auto px-3 py-1 bg-[#00FFFF]/10 text-[#00FFFF] rounded-full text-sm">
                  {selectedTicket.user_role === 'vendor' ? 'Vendeur' : 'Client'}
                </span>
              </div>

              {/* Controls */}
              <div className="flex flex-wrap gap-4">
                {/* Status selector */}
                <div className="flex-1 min-w-[200px]">
                  <label className="block text-xs text-[#6B6B6E] mb-1">Statut</label>
                  <select
                    value={selectedTicket.status}
                    onChange={(e) => handleStatusChange(e.target.value)}
                    className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-3 py-2 text-white focus:border-[#FF6B35] focus:outline-none"
                    style={{ color: status.color }}
                  >
                    {Object.entries(statusLabels).map(([key, val]) => (
                      <option key={key} value={key} style={{ color: val.color }}>
                        {val.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Priority selector */}
                <div className="flex-1 min-w-[200px]">
                  <label className="block text-xs text-[#6B6B6E] mb-1">Priorité</label>
                  <select
                    value={selectedTicket.priority}
                    onChange={(e) => handlePriorityChange(e.target.value)}
                    className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-3 py-2 text-white focus:border-[#FF6B35] focus:outline-none"
                    style={{ color: priority.color }}
                  >
                    {Object.entries(priorityLabels).map(([key, val]) => (
                      <option key={key} value={key} style={{ color: val.color }}>
                        {val.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Conversation */}
          <div className="bg-[#1A1A1D] border border-[#2A2A2D] rounded-xl overflow-hidden">
            {loadingTicket ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-[#FF6B35]" />
              </div>
            ) : (
              <TicketConversation 
                ticket={selectedTicket}
                messages={messages}
                onNewMessage={handleNewMessage}
                isAdmin={true}
              />
            )}
          </div>
        </div>
      </div>
    );
  }

  // Vue liste des tickets (Admin)
  return (
    <div className="min-h-screen bg-[#121214] py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white">Gestion des tickets</h1>
            <p className="text-[#B8B8BA] mt-1">Gérez les demandes de support</p>
          </div>
          <button
            onClick={fetchTickets}
            className="flex items-center gap-2 px-4 py-2 bg-[#222226] rounded-xl hover:bg-[#2A2A2D] transition-colors"
          >
            <RefreshCw className="h-5 w-5 text-[#B8B8BA]" />
            <span className="text-[#B8B8BA]">Actualiser</span>
          </button>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-8">
            <div className="bg-[#1A1A1D] border border-[#2A2A2D] rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <BarChart3 className="h-5 w-5 text-[#FF6B35]" />
                <span className="text-[#6B6B6E] text-sm">Total</span>
              </div>
              <p className="text-2xl font-bold text-white">{stats.total}</p>
            </div>
            <div className="bg-[#1A1A1D] border border-[#2A2A2D] rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <MessageSquare className="h-5 w-5 text-[#00FF88]" />
                <span className="text-[#6B6B6E] text-sm">Ouverts</span>
              </div>
              <p className="text-2xl font-bold text-[#00FF88]">{stats.by_status?.open || 0}</p>
            </div>
            <div className="bg-[#1A1A1D] border border-[#2A2A2D] rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <Loader2 className="h-5 w-5 text-[#FFB800]" />
                <span className="text-[#6B6B6E] text-sm">En cours</span>
              </div>
              <p className="text-2xl font-bold text-[#FFB800]">{stats.by_status?.in_progress || 0}</p>
            </div>
            <div className="bg-[#1A1A1D] border border-[#2A2A2D] rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="h-5 w-5 text-[#00FFFF]" />
                <span className="text-[#6B6B6E] text-sm">Attente</span>
              </div>
              <p className="text-2xl font-bold text-[#00FFFF]">{stats.by_status?.waiting_response || 0}</p>
            </div>
            <div className="bg-[#1A1A1D] border border-[#FF3B5C]/30 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <AlertCircle className="h-5 w-5 text-[#FF3B5C]" />
                <span className="text-[#6B6B6E] text-sm">Urgents</span>
              </div>
              <p className="text-2xl font-bold text-[#FF3B5C]">{stats.active_urgent || 0}</p>
            </div>
            <div className="bg-[#1A1A1D] border border-[#FF6B35]/30 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="h-5 w-5 text-[#FF6B35]" />
                <span className="text-[#6B6B6E] text-sm">Haute prio</span>
              </div>
              <p className="text-2xl font-bold text-[#FF6B35]">{stats.active_high || 0}</p>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-[#1A1A1D] border border-[#2A2A2D] rounded-xl p-4 mb-6">
          <div className="flex flex-wrap gap-4">
            {/* Search */}
            <div className="flex-1 min-w-[250px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#6B6B6E]" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Rechercher par numéro, sujet, email..."
                  className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg pl-10 pr-4 py-2 text-white placeholder-[#6B6B6E] focus:border-[#FF6B35] focus:outline-none"
                />
              </div>
            </div>

            {/* Status filter */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-2 text-white focus:border-[#FF6B35] focus:outline-none"
            >
              <option value="all">Tous les statuts</option>
              {Object.entries(statusLabels).map(([key, val]) => (
                <option key={key} value={key}>{val.label}</option>
              ))}
            </select>

            {/* Category filter */}
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-2 text-white focus:border-[#FF6B35] focus:outline-none"
            >
              <option value="all">Toutes catégories</option>
              {Object.entries(categoryLabels).map(([key, val]) => (
                <option key={key} value={key}>{val.label}</option>
              ))}
            </select>

            {/* Priority filter */}
            <select
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value)}
              className="bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-2 text-white focus:border-[#FF6B35] focus:outline-none"
            >
              <option value="all">Toutes priorités</option>
              {Object.entries(priorityLabels).map(([key, val]) => (
                <option key={key} value={key}>{val.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Tickets list */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-[#FF6B35]" />
          </div>
        ) : filteredTickets.length === 0 ? (
          <div className="text-center py-12 bg-[#1A1A1D] border border-[#2A2A2D] rounded-xl">
            <MessageSquare className="h-16 w-16 text-[#3A3A3D] mx-auto mb-4" />
            <p className="text-[#B8B8BA]">Aucun ticket trouvé</p>
          </div>
        ) : (
          <div className="bg-[#1A1A1D] border border-[#2A2A2D] rounded-xl overflow-hidden">
            <table className="w-full">
              <thead className="bg-[#222226]">
                <tr>
                  <th className="text-left px-4 py-3 text-[#6B6B6E] font-medium text-sm">Ticket</th>
                  <th className="text-left px-4 py-3 text-[#6B6B6E] font-medium text-sm">Client</th>
                  <th className="text-left px-4 py-3 text-[#6B6B6E] font-medium text-sm hidden md:table-cell">Catégorie</th>
                  <th className="text-left px-4 py-3 text-[#6B6B6E] font-medium text-sm">Priorité</th>
                  <th className="text-left px-4 py-3 text-[#6B6B6E] font-medium text-sm">Statut</th>
                  <th className="text-left px-4 py-3 text-[#6B6B6E] font-medium text-sm hidden lg:table-cell">Mise à jour</th>
                </tr>
              </thead>
              <tbody>
                {filteredTickets.map((ticket) => {
                  const status = statusLabels[ticket.status] || statusLabels.open;
                  const priority = priorityLabels[ticket.priority] || priorityLabels.medium;
                  const category = categoryLabels[ticket.category] || categoryLabels.other;

                  return (
                    <tr 
                      key={ticket.id}
                      onClick={() => navigate(`/admin/tickets/${ticket.id}`)}
                      className="border-t border-[#2A2A2D] hover:bg-[#222226] cursor-pointer transition-colors"
                    >
                      <td className="px-4 py-4">
                        <p className="text-[#FF6B35] font-mono text-sm">{ticket.ticket_number}</p>
                        <p className="text-white font-medium truncate max-w-[200px]">{ticket.subject}</p>
                      </td>
                      <td className="px-4 py-4">
                        <p className="text-white text-sm">{ticket.user_name}</p>
                        <p className="text-[#6B6B6E] text-xs">{ticket.user_email}</p>
                      </td>
                      <td className="px-4 py-4 hidden md:table-cell">
                        <span 
                          className="px-2 py-1 rounded text-xs font-medium"
                          style={{ backgroundColor: `${category.color}20`, color: category.color }}
                        >
                          {category.label}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <span 
                          className="px-2 py-1 rounded text-xs font-medium"
                          style={{ backgroundColor: `${priority.color}20`, color: priority.color }}
                        >
                          {priority.label}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <div 
                          className="flex items-center gap-1.5 px-2 py-1 rounded w-fit text-xs font-medium"
                          style={{ backgroundColor: `${status.color}20`, color: status.color }}
                        >
                          {status.icon}
                          {status.label}
                        </div>
                      </td>
                      <td className="px-4 py-4 hidden lg:table-cell">
                        <p className="text-[#6B6B6E] text-sm">{formatDate(ticket.updated_at)}</p>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminTickets;
